import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Download, ShoppingCart, PlusCircle, UserPlus, Users } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import MintedStatusBadge from "./MintedStatusBadge";
import GibNiftyBadge from "./GibNiftyBadge";
import GibRatingBadge from "./GibRatingBadge";
import ChannelGibletBadge from "./ChannelGibletBadge";
import MemberCountBadge from "./MemberCountBadge";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";
import { useMarketplaceState } from "@/hooks/useMarketplaceState";

interface Solution {
  id: string;
  title: string;
  channel: string;
  creator: string;
  commercialType: "Sale" | "Lease" | "Collaborate" | "Stake" | "Follow";
  category: string;
  price: number;
  description: string;
  image?: string;
  isMinted?: boolean;
  assetType?: "kb" | "solution" | "gibnifty" | "channel";
  gibRating?: number;
  userTitle?: string;
  userId?: string;
  channelId?: string;
  channelSlug?: string;
  memberCount?: number;
  isPrivate?: boolean;
}

// Map channel slugs to their route paths
const getChannelRoute = (slug: string): string => {
  const channelRouteMap: Record<string, string> = {
    'ispai': '/ispai-channel',
    'curt': '/curt-channel',
    'oil-and-gas': '/oil-gas-channel',
    'us-federal-government': '/usa-channel',
    'utthunga': '/utthunga-channel',
    'education': '/education-channel',
  };
  return channelRouteMap[slug] || `/channels`;
};

const getCommercialTypeColor = (type: string) => {
  switch (type) {
    case "Sale":
      return "bg-blue-100 text-blue-800";
    case "Lease":
      return "bg-green-100 text-green-800";
    case "Collaborate":
      return "bg-purple-100 text-purple-800";
    case "Stake":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const getCategoryColor = (category: string) => {
  if (category.includes("Construction")) return "bg-red-100 text-red-800";
  if (category.includes("Project")) return "bg-blue-100 text-blue-800";
  if (category.includes("Engineering")) return "bg-indigo-100 text-indigo-800";
  if (category.includes("Green")) return "bg-green-100 text-green-800";
  if (category.includes("Automation")) return "bg-purple-100 text-purple-800";
  if (category.includes("Health")) return "bg-orange-100 text-orange-800";
  if (category.includes("Data")) return "bg-cyan-100 text-cyan-800";
  if (category.includes("AI")) return "bg-violet-100 text-violet-800";
  if (category.includes("Machine")) return "bg-fuchsia-100 text-fuchsia-800";
  if (category.includes("Upstream")) return "bg-emerald-100 text-emerald-800";
  if (category.includes("Downstream")) return "bg-amber-100 text-amber-800";
  if (category.includes("Midstream")) return "bg-teal-100 text-teal-800";
  return "bg-gray-100 text-gray-800";
};

type Props = {
  initialSolutions: Solution[];
};

const IspaiMarketplaceView: React.FC<Props> = ({ initialSolutions }) => {
  // Marketplace state persistence
  const { getInitialState, saveState, restoreScrollPosition } = useMarketplaceState();
  const initialState = getInitialState();
  
  const [searchQuery, setSearchQuery] = useState(initialState.searchQuery || "");
  const [viewType, setViewType] = useState<"list" | "grid">(initialState.viewType || "list");
  const [cart, setCart] = useState<Solution[]>([]);
  const [searchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const { openProfileDrawer } = useProfileDrawer();
  const navigate = useNavigate();

  const filteredSolutions = useMemo(() => {
    return initialSolutions.filter((s) =>
      [s.title, s.creator, s.category, s.description]
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  }, [initialSolutions, searchQuery]);

  useEffect(() => {
    const q = searchParams.get("q");
    const id = searchParams.get("id");
    if (q) setSearchQuery(q);
    if (id) {
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 3000);
      setTimeout(() => {
        const el = document.querySelector(`[data-solution-id="${id}"]`) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }
    
    // Restore scroll position on mount
    restoreScrollPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save state before navigating away
  const handleNavigateAway = useCallback(() => {
    saveState({
      viewType,
      selectedCategory: "all",
      commercialType: "all",
      searchQuery,
    });
  }, [viewType, searchQuery, saveState]);

  const addToCart = (solution: Solution) => {
    setCart((prev) => (prev.some((i) => i.id === solution.id) ? prev : [...prev, solution]));
  };

  const removeFromCart = (id: string) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
  };

  const totalPrice = useMemo(() => cart.reduce((t, i) => t + i.price, 0), [cart]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-black mb-2">Marketplace</h1>
            <p className="text-gray-600">Discover solutions from all channels in the iSPAI ecosystem</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="gap-2">
              <Download className="h-4 w-4" />
              Export
            </Button>
            <Button variant="outline" className="gap-2">
              <ShoppingCart className="h-4 w-4" />
              Cart
            </Button>
          </div>
        </div>

        <section className="mb-6">
          <h2 className="text-2xl font-semibold text-black">Knowledge Network and Marketplace</h2>
          <p className="text-gray-600">Browse 450+ specialized knowledge bases created by government officials, diplomats, and domain experts</p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-9">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="relative w-full max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search solutions..."
                      className="pl-10"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant={viewType === "list" ? "default" : "outline"} onClick={() => setViewType("list")} className="px-3">
                      List
                    </Button>
                    <Button variant={viewType === "grid" ? "default" : "outline"} onClick={() => setViewType("grid")} className="px-3">
                      Grid
                    </Button>
                  </div>
                </div>

                {viewType === "list" && (
                  <div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Solution</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Channel</TableHead>
                          <TableHead>Creator</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSolutions.map((solution) => (
                          <TableRow 
                            key={solution.id} 
                            data-solution-id={solution.id} 
                            className={`${highlightId === solution.id ? "ring-2 ring-primary rounded-md" : ""} ${solution.assetType === 'gibnifty' || solution.assetType === 'channel' ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                            onClick={() => {
                              if (solution.assetType === 'gibnifty' && solution.userId) {
                                openProfileDrawer(solution.userId);
                              } else if (solution.assetType === 'channel' && solution.channelSlug) {
                                handleNavigateAway();
                                navigate(getChannelRoute(solution.channelSlug));
                              }
                            }}
                          >
                            <TableCell className="align-top">
                              <div className="flex items-center gap-2">
                                {(solution.assetType === 'gibnifty' || solution.assetType === 'channel') && solution.image && (
                                  <img 
                                    src={solution.image} 
                                    alt={solution.title} 
                                    className={`w-8 h-8 object-cover ${solution.assetType === 'gibnifty' ? 'rounded-full' : 'rounded'}`}
                                  />
                                )}
                                <div>
                                  <div className="font-medium text-black">{solution.title}</div>
                                  {solution.assetType === 'gibnifty' && solution.userTitle && (
                                    <div className="text-xs text-muted-foreground">{solution.userTitle}</div>
                                  )}
                                  {solution.assetType === 'channel' && (
                                    <div className="text-xs text-muted-foreground">
                                      {solution.isPrivate ? 'Private' : 'Public'} • {solution.memberCount} {solution.memberCount === 1 ? 'member' : 'members'}
                                    </div>
                                  )}
                                  {!solution.assetType && (
                                    <>
                                      <div className="text-xs text-muted-foreground mt-1">{solution.category}</div>
                                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{solution.description}</div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              {solution.assetType === 'gibnifty' ? (
                                <GibNiftyBadge />
                              ) : solution.assetType === 'channel' ? (
                                <ChannelGibletBadge />
                              ) : (
                                <MintedStatusBadge isMinted={solution.isMinted ?? false} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-muted text-muted-foreground">
                                {solution.channel.toUpperCase()}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{solution.creator}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={getCommercialTypeColor(solution.commercialType)}>
                                {solution.commercialType}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold">
                              {solution.assetType === 'gibnifty' || solution.assetType === 'channel' ? 'Free' : `$${solution.price.toLocaleString()}`}
                            </TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addToCart(solution);
                                }} 
                                disabled={cart.some((i) => i.id === solution.id)}
                                variant={solution.assetType === 'gibnifty' || solution.assetType === 'channel' ? 'outline' : 'default'}
                              >
                                {cart.some((i) => i.id === solution.id) 
                                  ? "Added" 
                                  : solution.assetType === 'gibnifty' 
                                    ? <><UserPlus className="h-4 w-4 mr-1" /> Follow</>
                                    : solution.assetType === 'channel'
                                      ? <><Users className="h-4 w-4 mr-1" /> Follow</>
                                      : "Add to Cart"
                                }
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {viewType === "grid" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredSolutions.map((solution) => (
                      <Card 
                        key={solution.id} 
                        data-solution-id={solution.id} 
                        className={`group overflow-hidden ${highlightId === solution.id ? "ring-2 ring-primary rounded-md" : ""} ${solution.assetType === 'gibnifty' || solution.assetType === 'channel' ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
                        onClick={() => {
                          if (solution.assetType === 'gibnifty' && solution.userId) {
                            openProfileDrawer(solution.userId);
                          } else if (solution.assetType === 'channel' && solution.channelSlug) {
                            handleNavigateAway();
                            navigate(getChannelRoute(solution.channelSlug));
                          }
                        }}
                      >
                        {solution.image && (
                          <div className="overflow-hidden h-40">
                            <img
                              src={solution.image}
                              alt={`${solution.title} cover`}
                              className={`w-full h-40 object-cover transition-transform duration-300 group-hover:scale-110 ${solution.assetType === 'gibnifty' ? 'object-top' : solution.assetType === 'channel' ? 'object-contain bg-gradient-to-br from-slate-50 to-slate-100 p-4' : ''}`}
                              loading="lazy"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <div className="flex flex-wrap gap-2 items-center mb-3">
                            {solution.assetType === 'gibnifty' ? (
                              <GibNiftyBadge />
                            ) : solution.assetType === 'channel' ? (
                              <ChannelGibletBadge />
                            ) : (
                              <MintedStatusBadge isMinted={solution.isMinted ?? false} />
                            )}
                            {solution.assetType === 'gibnifty' && solution.gibRating !== undefined ? (
                              <GibRatingBadge rating={solution.gibRating} />
                            ) : solution.assetType === 'channel' && solution.memberCount !== undefined ? (
                              <MemberCountBadge count={solution.memberCount} />
                            ) : (
                              <Badge variant="outline" className={getCategoryColor(solution.category)}>
                                {solution.category}
                              </Badge>
                            )}
                            <Badge variant="outline" className={`ml-auto ${getCommercialTypeColor(solution.commercialType)}`}>
                              {solution.commercialType}
                            </Badge>
                          </div>
                          <h3 className="font-medium mb-1">{solution.title}</h3>
                          {solution.assetType === 'gibnifty' && solution.userTitle && (
                            <p className="text-xs text-muted-foreground mb-1">{solution.userTitle}</p>
                          )}
                          {solution.assetType === 'channel' && (
                            <p className="text-xs text-muted-foreground mb-1">
                              {solution.isPrivate ? '🔒 Private' : '🌐 Public'} Channel
                            </p>
                          )}
                          <p className="text-sm mb-4 text-muted-foreground line-clamp-2">{solution.description}</p>
                          {solution.assetType === 'gibnifty' && solution.channel && (
                            <p className="text-xs text-muted-foreground mb-2">
                              <span className="font-medium">Channels:</span> {solution.channel}
                            </p>
                          )}
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">
                              {solution.assetType === 'gibnifty' || solution.assetType === 'channel' ? 'Free' : `$${solution.price.toLocaleString()}`}
                            </span>
                            <Button 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(solution);
                              }} 
                              disabled={cart.some((i) => i.id === solution.id)}
                              variant={solution.assetType === 'gibnifty' || solution.assetType === 'channel' ? 'outline' : 'default'}
                            >
                              {cart.some((i) => i.id === solution.id) ? 
                                "Added" : 
                                solution.assetType === 'gibnifty' ? 
                                  <><UserPlus className="h-4 w-4" /></> : 
                                  solution.assetType === 'channel' ?
                                    <><Users className="h-4 w-4" /></> :
                                    <PlusCircle className="h-4 w-4" />
                              }
                            </Button>
                          </div>
                          {solution.assetType !== 'gibnifty' && solution.assetType !== 'channel' && (
                            <div className="mt-2 text-xs text-muted-foreground">Creator: {solution.creator}</div>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-3">
            <div className="sticky top-24">
              <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4" />
                  Your Cart ({cart.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Your cart is empty</p>
                ) : (
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.id} className="flex justify-between items-center text-sm border-b pb-2">
                        <div className="flex items-center gap-2">
                          {(item.assetType === 'gibnifty' || item.assetType === 'channel') && item.image && (
                            <img 
                              src={item.image} 
                              alt={item.title} 
                              className={`w-6 h-6 object-cover ${item.assetType === 'gibnifty' ? 'rounded-full' : 'rounded'}`} 
                            />
                          )}
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <p className="text-muted-foreground">
                              {item.assetType === 'gibnifty' || item.assetType === 'channel' ? 'Follow' : `$${item.price.toLocaleString()}`}
                            </p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeFromCart(item.id)}>
                          ×
                        </Button>
                      </div>
                    ))}
                    <div className="pt-2 border-t">
                      <div className="flex justify-between font-medium">
                        <span>Total:</span>
                        <span>${""}{totalPrice.toLocaleString()}</span>
                      </div>
                      <Button className="w-full mt-4">Checkout</Button>
                    </div>
                  </div>
                )}
              </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default IspaiMarketplaceView;
